import React, { useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useAuth0 } from '@auth0/auth0-react';
import {
  EmailLabelRule,
  CreateEmailLabelRuleRequest,
  CreateEmailLabelRuleResponse,
  UpdateEmailLabelRuleRequest,
  UpdateEmailLabelRuleResponse,
  DeleteEmailLabelRuleResponse,
} from '@dreamai/types';
import api from '../../../api';
import { ReactComponent as PlusIcon } from '../../../icons/plus-small.svg';
import { ReactComponent as MailIcon } from '../../../icons/mail-05.svg';
import { ReactComponent as CheckCircleFilledIcon } from '../../../icons/check-circle-filled.svg';
import '../canvas/inbound-email-wrapper.scss';
import './email-tag-rules.scss';

interface EmailTagRulesProps {
  rules: EmailLabelRule[];
  onRulesChange: (rules: EmailLabelRule[]) => void;
  /** Render the section header. Set to false when the host already provides a header. */
  showHeader?: boolean;
  /** Switch the host to the Inboxes tab (used by the exceptions callout). */
  onGoToInboxes?: () => void;
}

/** Known built-in classification keys with display names and backend defaults */
export const BUILT_IN_CLASSIFICATIONS: {
  key: string;
  label: string;
  description: string;
  defaultLabelName: string;
  defaultColor: { bg: string; text: string };
  defaultArchive: boolean;
}[] = [
  {
    key: 'spam',
    label: 'Spam',
    description: 'Any obvious scams or phishing',
    defaultLabelName: 'Junk',
    defaultColor: { bg: '#f691b2', text: '#711a36' },
    defaultArchive: true,
  },
  {
    key: 'promotional',
    label: 'Promotional',
    description: 'Marketing, newsletters, and mass-mailing',
    defaultLabelName: 'Promotional',
    defaultColor: { bg: '#fad165', text: '#594c05' },
    defaultArchive: true,
  },
  {
    key: 'receipt',
    label: 'Receipt',
    description: 'Receipts and payment confirmations',
    defaultLabelName: 'Receipt',
    defaultColor: { bg: '#a4c2f4', text: '#0d3472' },
    defaultArchive: true,
  },
  {
    key: 'calendar_invite',
    label: 'Calendar invite',
    description: 'Meeting and calendar invitations',
    defaultLabelName: 'Calendar',
    defaultColor: { bg: '#b3efd3', text: '#0b4f30' },
    defaultArchive: false,
  },
  {
    key: 'booking_confirmation',
    label: 'Booking confirmation',
    description: 'Hotel, flight and restaurant bookings',
    defaultLabelName: 'Receipt',
    defaultColor: { bg: '#a4c2f4', text: '#0d3472' },
    defaultArchive: false,
  },
];

/** Gmail system/reserved label names that cannot be used for user labels */
export const RESERVED_GMAIL_LABEL_NAMES = new Set(
  [
    'important',
    'starred',
    'spam',
    'trash',
    'drafts',
    'sent',
    'inbox',
    'unread',
    'chat',
    'category_personal',
    'category_social',
    'category_promotions',
    'category_updates',
    'category_forums',
  ].map((s) => s.toLowerCase()),
);

export function isReservedLabelName(name: string): boolean {
  return RESERVED_GMAIL_LABEL_NAMES.has(name.trim().toLowerCase());
}

/**
 * Gmail pre-approved label colour pairs.
 * All values verified against the Gmail API v1 predefined color set:
 * https://developers.google.com/resources/api-libraries/documentation/gmail/v1/python/latest/gmail_v1.users.labels.html
 */
export const GMAIL_LABEL_COLORS: { bg: string; text: string }[] = [
  { bg: '#fb4c2f', text: '#ffffff' },
  { bg: '#e66550', text: '#ffffff' },
  { bg: '#efa093', text: '#000000' },
  { bg: '#ff7537', text: '#ffffff' },
  { bg: '#ffad46', text: '#ffffff' },
  { bg: '#fad165', text: '#594c05' },
  { bg: '#fbe983', text: '#594c05' },
  { bg: '#16a766', text: '#ffffff' },
  { bg: '#43d692', text: '#ffffff' },
  { bg: '#b3efd3', text: '#0b4f30' },
  { bg: '#2da2bb', text: '#ffffff' },
  { bg: '#98d7e4', text: '#0d3b44' },
  { bg: '#4a86e8', text: '#ffffff' },
  { bg: '#a4c2f4', text: '#0d3472' },
  { bg: '#a479e2', text: '#ffffff' },
  { bg: '#b99aff', text: '#ffffff' },
  { bg: '#e3d7ff', text: '#3d188e' },
  { bg: '#f691b2', text: '#711a36' },
  { bg: '#fcdee8', text: '#711a36' },
  { bg: '#cca6ac', text: '#662e37' },
  { bg: '#ebdbde', text: '#662e37' },
];

type BuiltInDisplayItem = (typeof BUILT_IN_CLASSIFICATIONS)[number] & {
  rule?: EmailLabelRule;
};

const EmailTagRules: React.FC<EmailTagRulesProps> = ({
  rules,
  onRulesChange,
  showHeader = true,
  onGoToInboxes,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingRule, setEditingRule] = useState<EmailLabelRule | null>(null);

  const builtInRules = useMemo(
    () => rules.filter((r) => r.classification !== null),
    [rules],
  );
  const customRules = useMemo(
    () => rules.filter((r) => r.description !== null),
    [rules],
  );

  const builtInDisplay = useMemo<BuiltInDisplayItem[]>(
    () =>
      BUILT_IN_CLASSIFICATIONS.map((bc) => ({
        ...bc,
        rule: builtInRules.find((r) => r.classification === bc.key),
      })),
    [builtInRules],
  );

  const createRule = async (
    body: CreateEmailLabelRuleRequest,
  ): Promise<EmailLabelRule | null> => {
    try {
      const token = await getAccessTokenSilently();
      const response = await api.post<CreateEmailLabelRuleResponse>(
        '/email-label-rules',
        body,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (response.data.success && response.data.rule) {
        onRulesChange([...rules, response.data.rule]);
        return response.data.rule;
      }
    } catch (err) {
      console.error('Error creating rule:', err);
    }
    return null;
  };

  const handleCreateFromModal = async (body: CreateEmailLabelRuleRequest) => {
    const created = await createRule(body);
    if (created) {
      setShowCreateModal(false);
    }
  };

  const handleCreateAndEdit = async (item: BuiltInDisplayItem) => {
    const created = await createRule({
      classification: item.key,
      labelName: item.defaultLabelName,
      labelColor: {
        backgroundColor: item.defaultColor.bg,
        textColor: item.defaultColor.text,
      },
      archive: item.defaultArchive,
    });
    if (created) {
      setEditingRule(created);
    }
  };

  const handleUpdateRule = async (
    ruleId: string,
    body: UpdateEmailLabelRuleRequest,
  ) => {
    try {
      const token = await getAccessTokenSilently();
      const response = await api.patch<UpdateEmailLabelRuleResponse>(
        `/email-label-rules/${ruleId}`,
        body,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (response.data.success && response.data.rule) {
        onRulesChange(
          rules.map((r) => (r.id === ruleId ? response.data.rule! : r)),
        );
        setEditingRule(null);
      }
    } catch (err) {
      console.error('Error updating rule:', err);
    }
  };

  const handleDeleteRule = async (ruleId: string) => {
    try {
      const token = await getAccessTokenSilently();
      const response = await api.delete<DeleteEmailLabelRuleResponse>(
        `/email-label-rules/${ruleId}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (response.data.success) {
        onRulesChange(rules.filter((r) => r.id !== ruleId));
        setEditingRule(null);
      }
    } catch (err) {
      console.error('Error deleting rule:', err);
    }
  };

  const toggleArchive = async (
    item: BuiltInDisplayItem,
    nextArchive: boolean,
  ) => {
    if (item.rule) {
      await handleUpdateRule(item.rule.id, { archive: nextArchive });
    } else {
      await createRule({
        classification: item.key,
        labelName: item.defaultLabelName,
        labelColor: {
          backgroundColor: item.defaultColor.bg,
          textColor: item.defaultColor.text,
        },
        archive: nextArchive,
      });
    }
  };

  const toggleCustomArchive = async (
    rule: EmailLabelRule,
    nextArchive: boolean,
  ) => {
    await handleUpdateRule(rule.id, { archive: nextArchive });
  };

  const handleTagPillClick = (item: BuiltInDisplayItem) => {
    if (item.rule) {
      setEditingRule(item.rule);
    } else {
      handleCreateAndEdit(item);
    }
  };

  const addTagRuleButton = (
    <button
      type='button'
      className='btn btn-icon'
      onClick={() => setShowCreateModal(true)}
      data-testid='add-tag-rule'
    >
      <PlusIcon />
      Add tag rule
    </button>
  );

  return (
    <div className='email-tag-rules'>
      {showHeader ? (
        <div className='section-header'>
          <h2>Your tags</h2>
          <span className='spacer' />
          {addTagRuleButton}
        </div>
      ) : (
        <div className='email-tag-rules-add-minimal'>{addTagRuleButton}</div>
      )}

      <div className='rules-list'>
        <div className='rules-group'>
          <span className='rules-group-label'>
            Tags your secretary has created
          </span>
          {builtInDisplay.map((item) => {
            const archive = item.rule ? item.rule.archive : item.defaultArchive;
            const labelName = item.rule
              ? item.rule.labelName
              : item.defaultLabelName;
            const bg =
              item.rule?.labelColor?.backgroundColor ?? item.defaultColor.bg;
            const text =
              item.rule?.labelColor?.textColor ?? item.defaultColor.text;

            return (
              <TagRuleRow
                key={item.key}
                bg={bg}
                text={text}
                title={item.label}
                subtitle={item.description}
                labelName={labelName}
                archive={archive}
                onEdit={() => handleTagPillClick(item)}
                onToggleArchive={() => toggleArchive(item, !archive)}
                testId={`tag-rule-${item.key}`}
              />
            );
          })}
        </div>

        {onGoToInboxes && (
          <div className='rules-callout'>
            <span className='rules-callout-icon'>
              <MailIcon />
            </span>
            <span>
              <b>Need more control?</b> Set exceptions to your rules{' '}
              <button
                type='button'
                className='btn-link'
                onClick={onGoToInboxes}
              >
                on the Inboxes tab
              </button>
            </span>
          </div>
        )}

        {customRules.length > 0 && (
          <div className='rules-group'>
            <span className='rules-group-label'>Tags you&apos;ve created</span>
            {customRules.map((rule) => (
              <TagRuleRow
                key={rule.id}
                bg={rule.labelColor?.backgroundColor ?? '#e6e8eb'}
                text={rule.labelColor?.textColor ?? '#374562'}
                title={rule.description ?? rule.labelName}
                subtitle='You created'
                labelName={rule.labelName}
                archive={rule.archive}
                onEdit={() => setEditingRule(rule)}
                onToggleArchive={() => toggleCustomArchive(rule, !rule.archive)}
                testId={`tag-rule-${rule.id}`}
              />
            ))}
          </div>
        )}
      </div>

      {showCreateModal && (
        <CreateRuleModal
          onClose={() => setShowCreateModal(false)}
          onCreate={handleCreateFromModal}
        />
      )}

      {editingRule && (
        <EditRuleModal
          rule={editingRule}
          onClose={() => setEditingRule(null)}
          onSave={(body) => handleUpdateRule(editingRule.id, body)}
          onDelete={() => handleDeleteRule(editingRule.id)}
        />
      )}
    </div>
  );
};

export default EmailTagRules;

// ── Tag Rule Row ────────────────────────────────────────────────────

interface TagRuleRowProps {
  bg: string;
  text: string;
  title: string;
  subtitle: string;
  labelName: string;
  archive: boolean;
  onEdit: () => void;
  onToggleArchive: () => void;
  testId: string;
}

/** A whole tag row is hoverable and clickable to edit; the archive control is not. */
const TagRuleRow: React.FC<TagRuleRowProps> = ({
  bg,
  text,
  title,
  subtitle,
  labelName,
  archive,
  onEdit,
  onToggleArchive,
  testId,
}) => (
  <div
    className='rule-row'
    style={{ '--rule-bg': bg, '--rule-text': text } as React.CSSProperties}
    role='button'
    tabIndex={0}
    onClick={onEdit}
    onKeyDown={(e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onEdit();
      }
    }}
    aria-label={`Edit ${title} tag rule`}
    data-testid={testId}
  >
    <div className='rule-info'>
      <span className='rule-desc'>{title}</span>
      <span className='rule-subdesc'>{subtitle}</span>
    </div>
    <div className='rule-controls'>
      <span className='rule-tagged-as'>Tagged as</span>
      <span className='tag-pill'>{labelName}</span>
      <button
        type='button'
        className={`archive-toggle ${archive ? 'on' : ''}`}
        aria-pressed={archive}
        onClick={(e) => {
          e.stopPropagation();
          onToggleArchive();
        }}
        data-testid={`${testId}-archive`}
      >
        <span className='archive-label'>Archive</span>
        <span className='archive-thumb'>
          {archive ? (
            <CheckCircleFilledIcon className='archive-icon' />
          ) : (
            <span className='archive-icon archive-icon-empty' />
          )}
        </span>
      </button>
    </div>
  </div>
);

// ── Create Rule Modal ────────────────────────────────────────────────

interface CreateRuleModalProps {
  onClose: () => void;
  onCreate: (body: CreateEmailLabelRuleRequest) => void;
}

const CreateRuleModal: React.FC<CreateRuleModalProps> = ({
  onClose,
  onCreate,
}) => {
  const [description, setDescription] = useState('');
  const [labelName, setLabelName] = useState('');
  const [selectedColor, setSelectedColor] = useState(GMAIL_LABEL_COLORS[0]);
  const [colourPickerOpen, setColourPickerOpen] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!description.trim() || !labelName.trim()) return;
    onCreate({
      description: description.trim(),
      labelName: labelName.trim(),
      labelColor: {
        backgroundColor: selectedColor.bg,
        textColor: selectedColor.text,
      },
      archive: false,
    });
  };

  return (
    <Modal show onHide={onClose} centered dialogClassName='rule-modal'>
      <Modal.Header closeButton>
        <Modal.Title>Add Rule</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <form id='create-rule-form' onSubmit={handleSubmit}>
          <div className='form-group'>
            <label htmlFor='create-description'>
              What sort of emails does this rule affect?
            </label>
            <span className='form-hint'>
              You can specify email addresses or domains, or describe the sender
              or contents.
            </span>
            <textarea
              id='create-description'
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder='e.g. Emails from suppliers with invoices attached'
              rows={3}
            />
          </div>
          <div className='form-group'>
            <label htmlFor='create-label'>What tag should be applied?</label>
            <input
              id='create-label'
              type='text'
              value={labelName}
              onChange={(e) => setLabelName(e.target.value)}
              placeholder='e.g. Supplier Invoices'
            />
            {isReservedLabelName(labelName) && (
              <span className='form-error'>
                &ldquo;{labelName.trim()}&rdquo; can&apos;t be used as it is a
                reserved Google label name. Please choose a different name.
              </span>
            )}
          </div>
          <div className='form-group'>
            <label>Tag colour</label>
            <div className='colour-picker'>
              <button
                type='button'
                className='colour-picker-trigger'
                style={{
                  backgroundColor: selectedColor.bg,
                  color: selectedColor.text,
                }}
                onClick={() => setColourPickerOpen(!colourPickerOpen)}
              >
                {labelName.trim() || '\u00A0'}
              </button>
              {colourPickerOpen && (
                <div className='colour-picker-dropdown'>
                  {GMAIL_LABEL_COLORS.map((c) => (
                    <button
                      key={c.bg}
                      type='button'
                      className={`colour-picker-option ${selectedColor.bg === c.bg ? 'selected' : ''}`}
                      style={{ backgroundColor: c.bg, color: c.text }}
                      onClick={() => {
                        setSelectedColor(c);
                        setColourPickerOpen(false);
                      }}
                    >
                      {labelName.trim() || '\u00A0'}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </form>
      </Modal.Body>
      <Modal.Footer>
        <button type='button' onClick={onClose}>
          Cancel
        </button>
        <button
          type='submit'
          form='create-rule-form'
          className='action-btn'
          disabled={
            !description.trim() ||
            !labelName.trim() ||
            isReservedLabelName(labelName)
          }
        >
          Save Rule
        </button>
      </Modal.Footer>
    </Modal>
  );
};

// ── Edit Rule Modal ────────────────────────────────────────────────

interface EditRuleModalProps {
  rule: EmailLabelRule;
  onClose: () => void;
  onSave: (body: UpdateEmailLabelRuleRequest) => void;
  onDelete: () => void;
}

const EditRuleModal: React.FC<EditRuleModalProps> = ({
  rule,
  onClose,
  onSave,
  onDelete,
}) => {
  const [labelName, setLabelName] = useState(rule.labelName);
  const [archive, setArchive] = useState(rule.archive);
  const [description, setDescription] = useState(rule.description ?? '');
  const [selectedColor, setSelectedColor] = useState(
    GMAIL_LABEL_COLORS.find((c) => c.bg === rule.labelColor?.backgroundColor) ??
      GMAIL_LABEL_COLORS[0],
  );
  const [colourPickerOpen, setColourPickerOpen] = useState(false);
  const isCustom = rule.classification === null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!labelName.trim()) return;
    const body: UpdateEmailLabelRuleRequest = {
      labelName: labelName.trim(),
      archive,
      labelColor: {
        backgroundColor: selectedColor.bg,
        textColor: selectedColor.text,
      },
    };
    if (isCustom) {
      body.description = description.trim() || null;
    }
    onSave(body);
  };

  return (
    <Modal show onHide={onClose} centered dialogClassName='rule-modal'>
      <Modal.Header closeButton>
        <Modal.Title>Edit Rule</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <form id='edit-rule-form' onSubmit={handleSubmit}>
          {isCustom && (
            <div className='form-group'>
              <label htmlFor='edit-description'>
                What sort of emails does this rule affect?
              </label>
              <span className='form-hint'>
                You can specify email addresses or domains, or describe the
                sender or contents.
              </span>
              <textarea
                id='edit-description'
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
              />
            </div>
          )}
          <div className='form-group'>
            <label htmlFor='edit-label'>What tag should be applied?</label>
            <input
              id='edit-label'
              type='text'
              value={labelName}
              onChange={(e) => setLabelName(e.target.value)}
            />
            {isReservedLabelName(labelName) && (
              <span className='form-error'>
                &ldquo;{labelName.trim()}&rdquo; is a reserved Gmail label name.
                Please choose a different name.
              </span>
            )}
          </div>
          <div className='form-group'>
            <label>Tag colour</label>
            <div className='colour-picker'>
              <button
                type='button'
                className='colour-picker-trigger'
                style={{
                  backgroundColor: selectedColor.bg,
                  color: selectedColor.text,
                }}
                onClick={() => setColourPickerOpen(!colourPickerOpen)}
              >
                {labelName.trim() || '\u00A0'}
              </button>
              {colourPickerOpen && (
                <div className='colour-picker-dropdown'>
                  {GMAIL_LABEL_COLORS.map((c) => (
                    <button
                      key={c.bg}
                      type='button'
                      className={`colour-picker-option ${selectedColor.bg === c.bg ? 'selected' : ''}`}
                      style={{ backgroundColor: c.bg, color: c.text }}
                      onClick={() => {
                        setSelectedColor(c);
                        setColourPickerOpen(false);
                      }}
                    >
                      {labelName.trim() || '\u00A0'}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          <label className='checkbox-label'>
            <input
              type='checkbox'
              checked={archive}
              onChange={(e) => setArchive(e.target.checked)}
            />
            Auto-archive emails with this rule
          </label>
        </form>
      </Modal.Body>
      <Modal.Footer>
        <button type='button' onClick={onClose}>
          Cancel
        </button>
        <div className='action-buttons'>
          <button type='button' className='delete-button' onClick={onDelete}>
            Delete Rule
          </button>
          <button
            type='submit'
            form='edit-rule-form'
            className='action-btn'
            disabled={!labelName.trim() || isReservedLabelName(labelName)}
          >
            Save Changes
          </button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};
